/*
 * Copyright (c) 2023. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */
import type { AbsoluteURL, AppURL } from '@poweralpha/common';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

interface PALocation<State> extends Location {
  pathname: AbsoluteURL;
  search: `?${string}` | '';
  state?: State;
}

interface PANavigateType<State> {
  navigate: (path: AppURL | number, stateUpdate?: State, replace?: boolean) => void;
  updateState: (stateUpdate: State, replace?: boolean) => void;
  updateSearch: (newSearch: URLSearchParams, replace?: boolean) => void;
  location: PALocation<State>;
}

export function usePANavigate<State>(): PANavigateType<State> {
  const nav = useNavigate();
  const location = useLocation();
  const stateRef = useRef(location.state);

  const navigate = useCallback(
    (path: AppURL | number, stateUpdate?: State, replace?: boolean) => {
      if (typeof path === 'number') return nav(path);
      nav(path, { state: { ...stateRef.current, ...stateUpdate }, replace });
    },
    [nav],
  );

  const updateState = useCallback(
    (stateUpdate: Partial<State>, replace?: boolean) => {
      nav(location.pathname + location.search, {
        state: { ...stateRef.current, ...stateUpdate },
        replace,
      });
    },
    [nav, location.pathname, location.search],
  );

  // The current pathname, not `.`: under a splat route (`runs/:id/*`) `.` resolves to the route
  // match and would drop the rest of the path (e.g. the open report tab).
  const pathnameRef = useRef(location.pathname);
  pathnameRef.current = location.pathname;
  const updateSearch = useCallback(
    (newSearch: URLSearchParams, replace?: boolean) => {
      nav(
        { pathname: pathnameRef.current, search: `?${newSearch.toString()}` },
        { replace, state: stateRef.current },
      );
    },
    [nav],
  );

  // Only recompute when router location truly changes
  const stableLocation = useMemo(
    () =>
      ({
        pathname: location.pathname,
        hash: location.hash,
        search: location.search,
        state: location.state,
      }) as PALocation<State>,
    [location.pathname, location.hash, location.search, location.state],
  );

  useEffect(() => {
    stateRef.current = location.state;
  }, [location.state]);

  return { navigate, updateState, updateSearch, location: stableLocation };
}

export default usePANavigate;
